import React, { useEffect, useRef, useState } from 'react';

// Achtergrondvideo die echt afspeelt.
// React schrijft het `muted`-attribuut niet naar de HTML (bekend React-gedrag),
// dus in de prerender staat <video autoplay> zonder muted en blokkeren browsers
// het afspelen. Daarom zetten we muted hier zelf via de ref en starten we
// play() pas als de video in beeld is. Meerdere clips spelen na elkaar (showreel);
// bij prefers-reduced-motion blijft alleen de poster staan.
export default function AutoplayVideo({ clips, poster, label, className = '', width = 1280, height = 720 }) {
  const ref = useRef(null);
  const [index, setIndex] = useState(0);
  // Poster pas zetten als de speler bijna in beeld is: een `poster`-attribuut wordt
  // door de browser direct opgehaald, ook voor video's onder de vouw. Lighthouse
  // 2026-10-30 telde beide homepage-posters (127 KB, prioriteit Medium) mee in het
  // LCP-venster. Zonder poster blijft de speler donker tot hij in beeld komt.
  const [near, setNear] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video || near) return undefined;
    if (!('IntersectionObserver' in window)) { setNear(true); return undefined; }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setNear(true); observer.disconnect(); }
    }, { rootMargin: '400px 0px' });
    observer.observe(video);
    return () => observer.disconnect();
  }, [near]);

  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');

    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;

    // preload="none" + poster: er wordt pas videodata gehaald zodra de speler in beeld
    // komt. Lighthouse 2026-10-30 telde ~224 KiB metadata-download van video's buiten
    // beeld mee in het LCP-venster.
    const play = () => {
      if (video.preload === 'none' && video.readyState === 0) video.load();
      video.play().catch(() => {});
    };
    if (!('IntersectionObserver' in window)) {
      play();
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) play();
      else video.pause();
    }, { threshold: 0.25 });
    observer.observe(video);
    return () => observer.disconnect();
  }, [index]);

  const single = clips.length === 1;
  const next = () => setIndex((i) => (i + 1) % clips.length);

  return (
    <video
      ref={ref}
      key={clips[index]}
      muted
      playsInline
      loop={single}
      onEnded={single ? undefined : next}
      preload={index === 0 ? 'none' : 'auto'}
      poster={index === 0 && near ? poster : undefined}
      style={{ backgroundColor: '#0f172a' }}
      width={width}
      height={height}
      className={className}
      aria-label={label}
    >
      <source src={clips[index]} type="video/mp4" />
    </video>
  );
}
